<template>
  <div class="wg-overview">
    <div
      v-if="ordersLoading"
      class="wg-overview__skeleton"
      aria-busy="true"
      aria-label="Loading overview"
    >
      <div class="wg-overview__skeleton-inner">
        <div class="wg-overview__skeleton-top-row">
          <div class="wg-overview__skeleton-panel wg-overview__skeleton-panel--lavender">
            <div class="wg-overview__skeleton-panel-copy">
              <div class="wg-overview__skeleton-line wg-overview__skeleton-line--heading" />
              <div class="wg-overview__skeleton-line wg-overview__skeleton-line--body" />
              <div class="wg-overview__skeleton-line wg-overview__skeleton-line--body wg-overview__skeleton-line--short" />
            </div>
            <div class="wg-overview__skeleton-pill" />
          </div>
          <div class="wg-overview__skeleton-panel wg-overview__skeleton-panel--bordered">
            <div class="wg-overview__skeleton-deliverables">
              <div class="wg-overview__skeleton-avatar" />
              <div class="wg-overview__skeleton-panel-copy">
                <div class="wg-overview__skeleton-line wg-overview__skeleton-line--heading" />
                <div class="wg-overview__skeleton-line wg-overview__skeleton-line--body" />
              </div>
            </div>
            <div class="wg-overview__skeleton-pill" />
          </div>
        </div>

        <section class="wg-overview__skeleton-section">
          <ul class="wg-overview__skeleton-timeline">
            <li v-for="n in 4" :key="'tl-' + n" class="wg-overview__skeleton-timeline-item">
              <div class="wg-overview__skeleton-dot" />
              <div class="wg-overview__skeleton-timeline-copy">
                <div class="wg-overview__skeleton-line wg-overview__skeleton-line--timeline-title" />
                <div class="wg-overview__skeleton-line wg-overview__skeleton-line--timeline-date" />
              </div>
            </li>
          </ul>
        </section>

        <div class="wg-overview__skeleton-stats">
          <div v-for="n in statSkeletonCount" :key="'st-' + n" class="wg-overview__skeleton-stat-card">
            <div class="wg-overview__skeleton-line wg-overview__skeleton-line--stat-value" />
            <div class="wg-overview__skeleton-line wg-overview__skeleton-line--stat-label" />
            <div class="wg-overview__skeleton-line wg-overview__skeleton-line--stat-sub" />
          </div>
        </div>
      </div>
    </div>

    <EmptyUI v-else-if="isEmpty" />

    <div v-else class="wg-overview--populated">
      <div class="wg-overview--populated__top-row">
        <div
          class="wg-overview--populated__panel wg-overview--populated__panel--lavender"
        >
          <div class="wg-overview--populated__panel-text">
            <h2 class="wg-overview--populated__heading">
              {{ wgOverviewHero.title }}
            </h2>
            <p class="wg-overview--populated__subtext" v-html="wgOverviewHero.body">
            </p>
            <div
              v-if="wgOverviewHeroPagerVisible"
              class="wg-overview--populated__hero-status-pager"
              role="navigation"
              :aria-label="'Project status messages, ' + wgOverviewHeroPagerLabel"
            >
              <button
                type="button"
                class="wg-overview--populated__hero-status-pager__angle"
                aria-label="Previous status"
                @click="stepWgOverviewHero(-1)"
              >
                <i class="material-icons wg-overview--populated__hero-status-pager__icon" aria-hidden="true">chevron_left</i>
              </button>
              <span class="wg-overview--populated__hero-status-pager__label">{{ wgOverviewHeroPagerLabel }}</span>
              <button
                type="button"
                class="wg-overview--populated__hero-status-pager__angle"
                aria-label="Next status"
                @click="stepWgOverviewHero(1)"
              >
                <i class="material-icons wg-overview--populated__hero-status-pager__icon" aria-hidden="true">chevron_right</i>
              </button>
            </div>
          </div>
          <div
            v-if="(wgOverviewHero.ctaLabel && wgOverviewHero.ctaRoute) || (wgOverviewHero.secondaryCtaLabel && wgOverviewHero.secondaryCtaRoute)"
            class="wg-overview--populated__hero-actions"
          >
            <router-link
              v-if="wgOverviewHero.ctaLabel && wgOverviewHero.ctaRoute"
              class="wg-overview--populated__btn wg-overview--populated__hero-btn"
              :class="overviewPrimaryCtaClass"
              :to="overviewPrimaryCtaTo"
            >
              <span class="wg-overview--populated__hero-btn-label">{{ wgOverviewHero.ctaLabel }}</span>
              <i class="material-icons">arrow_forward</i>
            </router-link>
            <router-link
              v-if="wgOverviewHero.secondaryCtaLabel && wgOverviewHero.secondaryCtaRoute"
              class="wg-overview--populated__btn wg-overview--populated__hero-btn"
              :class="overviewSecondaryCtaClass"
              :to="overviewSecondaryCtaTo"
            >
              <span class="wg-overview--populated__hero-btn-label">{{ wgOverviewHero.secondaryCtaLabel }}</span>
              <i class="material-icons">arrow_forward</i>
            </router-link>
          </div>
        </div>

        <div
          class="wg-overview--populated__panel wg-overview--populated__panel--bordered"
          :class="{ 'wg-overview--populated__panel--mission-control': whiteGloveMissionControlAdminUi }"
        >
          <template v-if="whiteGloveMissionControlAdminUi">
            <div class="wg-overview--populated__deliverables-header">
              <div class="wg-overview--populated__deliverables-left">
                <div class="wg-overview--populated__g-icon" aria-hidden="true">
                  <span class="wg-overview--populated__g-letter">G</span>
                </div>
                <div class="wg-overview--populated__panel-text">
                  <h2 class="wg-overview--populated__heading">Project deliverables</h2>
                  <p class="wg-overview--populated__subtext">Here you can find all your deliverables.</p>
                </div>
              </div>
              <a
                v-if="site.google_docs_folder_url"
                class="wg-overview--populated__btn wg-overview--populated__btn--blue"
                :href="site.google_docs_folder_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Google folder
              </a>
              <span v-else class="wg-overview--populated__folder-unavailable">Folder link not set yet</span>
            </div>
            <div class="wg-overview--populated__deliverables-edit-row">
              <div class="wg-overview--populated__folder-url-field-wrap">
                <v-text-field
                  v-model="googleFolderUrlDraft"
                  outlined
                  dense
                  hide-details
                  placeholder="Enter Google Docs folder URL"
                  color="grayColor"
                  class="wg-overview--populated__folder-url-field"
                />
              </div>
              <v-btn
                rounded
                color="profileAnchorColor"
                class="whiteColor--text text-none wg-overview--populated__save-folder-btn"
                @click="saveGoogleFolderUrl"
              >
                Save
              </v-btn>
            </div>
          </template>
          <template v-else>
            <div class="wg-overview--populated__deliverables-left">
              <div class="wg-overview--populated__g-icon" aria-hidden="true">
                <span class="wg-overview--populated__g-letter">G</span>
              </div>
              <div class="wg-overview--populated__panel-text">
                <h2 class="wg-overview--populated__heading">Project deliverables</h2>
                <p class="wg-overview--populated__subtext">Here you can find all your deliverables.</p>
              </div>
            </div>
            <a
              v-if="site.google_docs_folder_url"
              class="wg-overview--populated__btn wg-overview--populated__btn--blue"
              :href="site.google_docs_folder_url"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Google folder
            </a>
            <span v-else class="wg-overview--populated__folder-unavailable">Folder link not set yet</span>
          </template>
        </div>
      </div>

      <section class="wg-overview--populated__timeline-section" aria-label="Project timeline">

        <div class="wg-overview--populated__timeline-header">
          Project timeline
        </div>
        <ul class="wg-overview--populated__timeline">
          <li
            v-for="ev in visibleTimelineEvents"
            :key="ev.key"
            class="wg-overview--populated__timeline-item"
          >
            <span class="wg-overview--populated__timeline-dot" />
            <div class="wg-overview--populated__timeline-body">
              <div class="wg-overview--populated__timeline-title">{{ ev.title }}</div>
              <div v-if="ev.date" class="wg-overview--populated__timeline-date">{{ ev.date }}</div>
            </div>
          </li>
        </ul>
        <button
          v-if="hasCollapsedTimeline"
          type="button"
          class="wg-overview--populated__timeline-toggle"
          @click="showAllTimelineEvents = !showAllTimelineEvents"
        >
          {{ showAllTimelineEvents ? 'Show less' : 'Show more' }}
        </button>
      </section>

      <div class="wg-overview--populated__stats-row">
        <div
          class="wg-overview--populated__stat-card wg-overview--populated__stat-card--link"
          role="link"
          tabindex="0"
          @click="navigateToOverviewRoute('ContentPlansWhiteGlove')"
          @keydown.enter.prevent="navigateToOverviewRoute('ContentPlansWhiteGlove')"
          @keydown.space.prevent="navigateToOverviewRoute('ContentPlansWhiteGlove')"
        >
          <div class="wg-overview--populated__stat-value">{{ overviewStats.contentPlansCount }}</div>
          <div class="wg-overview--populated__stat-label d-flex align-center gap5">
            {{ overviewStats.contentPlansCount === 1 ? 'Content plan' : 'Content plans' }}
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="wg-overview--populated__stat-tooltip-trigger"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop.prevent
                >
                  <i class="material-icons-outlined" style="font-size: 1rem; color: var(--v-gray40Color-base);">info</i>
                </span>
              </template>
              <div class="white-bg-tooltip small-tooltip p-3" style="line-height: 1.5;">
                Content plans are keyword-targeted page recommendations generated from your site audit. Each plan outlines what content to create or optimize to improve your rankings.
              </div>
            </v-tooltip>
          </div>
          <div class="wg-overview--populated__stat-sublabel">From content audit</div>
        </div>
        <div
          class="wg-overview--populated__stat-card wg-overview--populated__stat-card--link"
          role="link"
          tabindex="0"
          @click="navigateToOverviewRoute('OrderHistoryWhiteGlove')"
          @keydown.enter.prevent="navigateToOverviewRoute('OrderHistoryWhiteGlove')"
          @keydown.space.prevent="navigateToOverviewRoute('OrderHistoryWhiteGlove')"
        >
          <div class="wg-overview--populated__stat-value">{{ overviewStats.contentOrdered }}</div>
          <div class="wg-overview--populated__stat-label d-flex align-center gap5">
            Content ordered
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="wg-overview--populated__stat-tooltip-trigger"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop.prevent
                >
                  <i class="material-icons-outlined" style="font-size: 1rem; color: var(--v-gray40Color-base);">info</i>
                </span>
              </template>
              <div class="white-bg-tooltip small-tooltip p-3" style="line-height: 1.5;">
                The total number of articles you've purchased.
              </div>
            </v-tooltip>
          </div>
          <div class="wg-overview--populated__stat-sublabel">
            across {{ overviewStats.ordersCount }} {{ overviewStats.ordersCount === 1 ? 'order' : 'orders' }}
          </div>
        </div>
        <div
          class="wg-overview--populated__stat-card wg-overview--populated__stat-card--link"
          role="link"
          tabindex="0"
          @click="navigateToOverviewRoute('AllContentWhiteGlove')"
          @keydown.enter.prevent="navigateToOverviewRoute('AllContentWhiteGlove')"
          @keydown.space.prevent="navigateToOverviewRoute('AllContentWhiteGlove')"
        >
          <div class="wg-overview--populated__stat-value">{{ overviewStats.articlesDelivered }}</div>
          <div class="wg-overview--populated__stat-label d-flex align-center gap5">
            Articles delivered
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="wg-overview--populated__stat-tooltip-trigger"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop.prevent
                >
                  <i class="material-icons-outlined" style="font-size: 1rem; color: var(--v-gray40Color-base);">info</i>
                </span>
              </template>
              <div class="white-bg-tooltip small-tooltip p-3" style="line-height: 1.5;">
                Articles that have been completed and are ready to publish on your site. 'In progress' shows how many are currently being written by our team.
              </div>
            </v-tooltip>
          </div>
          <div class="wg-overview--populated__stat-sublabel">{{ overviewStats.articlesInProgress }} in progress</div>
        </div>
        <template v-if="whiteGloveMissionControlAdminUi">
          <div
            class="wg-overview--populated__stat-card wg-overview--populated__stat-card--mission-control"
          >
            <div class="wg-overview--populated__stat-value">
              <template v-if="missionControlStatsLoading">—</template>
              <template v-else>{{ missionControlPopRunsTotal }}</template>
            </div>
            <div class="wg-overview--populated__stat-label">POP runs</div>
            <div class="wg-overview--populated__stat-sublabel">
              <template v-if="missionControlStatsLoading">—</template>
              <template v-else>{{ missionControlPopRunsInProgress }} in progress</template>
            </div>
          </div>
          <div
            class="wg-overview--populated__stat-card wg-overview--populated__stat-card--mission-control"
          >
            <div class="wg-overview--populated__stat-value">
              <template v-if="missionControlStatsLoading">—</template>
              <template v-else-if="missionControlMonthlySpendUsd != null">
                {{ formatCurrency(missionControlMonthlySpendUsd) }}/month
              </template>
              <template v-else>—</template>
            </div>
            <div class="wg-overview--populated__stat-label">Client spend</div>
            <div class="wg-overview--populated__stat-sublabel">
              <template v-if="missionControlOpenInvoice">
                {{ formatCurrency(missionControlOpenInvoice.amount) }} in open invoices
              </template>
              <template v-else>No open invoices</template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mixin } from '@/components/mixin'
import { formatCurrency as formatCurrencyUtil } from '@/utils'
import EmptyUI from './EmptyUI.vue'
import { auditHasCompletedOnboarding, parseWhiteGloveOrdersResponse } from '@/utils/whiteGloveSiteList'

const CONTENT_ORDER_TYPES = ['silo_order', 'quick_content_order']
function parseTimelineUtcMoment (value) {
  if (value == null || value === '') return null
  const m = moment.utc(value)
  return m.isValid() ? m : null
}

function orderSortTs (o) {
  if (!o) return 0
  if (o.order_date_timestamp) return o.order_date_timestamp
  if (o.order_date) {
    return dateToTimelineSortTs(o.order_date)
  }
  return 0
}

/** Fractional Unix seconds (aligns with API `order_date_timestamp` from Python). */
function dateToTimelineSortTs (value) {
  const m = parseTimelineUtcMoment(value)
  return m ? m.valueOf() / 1000 : 0
}

/** Unix timestamp for when an audit was paid; falls back to generic order sort. */
function auditOrderPaidSortTs (audit) {
  if (!audit || audit.type !== 'audit_order') return orderSortTs(audit)
  const paid = audit.order_paid_at || audit.orderPaidAt
  if (paid) {
    const m = parseTimelineUtcMoment(paid)
    if (m) return m.valueOf() / 1000
  }
  return orderSortTs(audit)
}

/** Unix timestamp for when a content order was paid; falls back to API order_date fields. */
function contentOrderPaidSortTs (o) {
  if (!o || !CONTENT_ORDER_TYPES.includes(o.type)) return orderSortTs(o)
  const paid = o.order_paid_at || o.orderPaidAt
  if (paid) {
    const m = parseTimelineUtcMoment(paid)
    if (m) return m.valueOf() / 1000
  }
  return orderSortTs(o)
}

/** Unix timestamp from content order completion; falls back to generic order sort. */
function contentOrderCompletedSortTs (o) {
  if (!o || !CONTENT_ORDER_TYPES.includes(o.type)) return orderSortTs(o)
  const raw = o.completed_at || o.completedAt
  if (raw) {
    const ts = dateToTimelineSortTs(raw)
    if (ts) return ts
  }
  return orderSortTs(o)
}

/** Total ordered keywords on a silo / quick content order (same as order history). */
function contentOrderKeywordCount (o) {
  const top = (o.selected_top_level_keyword_ids || []).length
  const sup = (o.selected_supporting_keyword_ids || []).length
  return top + sup
}

function contentOrderIsConfirmed (o) {
  const st = o?.order_status || o?.orderStatus || ''
  return ['paid', 'to_be_paid_by_invoice'].includes(st)
}

function contentOrderFulfillmentStatus (o) {
  return o.fulfillement_status || o.fulfillementStatus || ''
}

/** Matches `isOrderedLike` in ContentPlans.vue — plans not yet ordered are everything else. */
function siloContentPlanOrderedLike (silo) {
  return ['paid', 'to_be_paid_by_invoice'].includes(silo?.content_report_order?.order_status)
}

export default {
  name: 'OverviewWhiteGlove',
  components: { EmptyUI },
  mixins: [mixin],
  data () {
    return {
      ordersLoading: true,
      siteOrders: [],
      /** Silo count from getSilos (all plans, any payment state); null if fetch failed. */
      contentPlansSiloCount: null,
      /** Silos with no paid / invoice pending order yet; null if silos fetch failed. */
      contentPlansNotOrderedCount: null,
      siteProjectStatuses: {},
      googleFolderUrlDraft: '',
      missionControlStatsLoading: false,
      missionControlPopRunsTotal: 0,
      missionControlPopRunsInProgress: 0,
      missionControlMonthlySpendUsd: null,
      /** Index into `overview_messages` (or single primary hero); reset on reload / site change. */
      wgOverviewHeroIndex: 0,
      showAllTimelineEvents: false,
    }
  },
  watch: {
    'site.google_docs_folder_url': {
      handler (newValue) {
        this.googleFolderUrlDraft = newValue || ''
      },
      immediate: true,
    },
    '$route.params.siteId': {
      handler () {
        this.loadSiteOrders()
        this.loadMissionControlSiteStatsIfNeeded()
      },
    },
    'site.id': {
      handler (newId) {
        if (newId && this.whiteGloveMissionControlAdminUi) {
          this.loadMissionControlSiteStatsIfNeeded()
        }
      },
    },
    whiteGloveMissionControlAdminUi (v) {
      if (v) {
        this.loadMissionControlSiteStatsIfNeeded()
      } else {
        this.missionControlPopRunsTotal = 0
        this.missionControlPopRunsInProgress = 0
        this.missionControlMonthlySpendUsd = null
        this.missionControlStatsLoading = false
      }
    },
  },
  computed: {
    statSkeletonCount () {
      return this.whiteGloveMissionControlAdminUi ? 5 : 3
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    missionControlOpenInvoice () {
      const orders = this.siteWhiteGloveOrders || []
      let total = 0
      let any = false
      for (const o of orders) {
        const url = o.stripe_hosted_invoice_url
        const amt = o.stripe_hosted_invoice_amount
        if (url && amt != null && amt !== '') {
          const num = Number(amt)
          if (!Number.isFinite(num)) continue
          total += num
          any = true
        }
      }
      return any ? { amount: total } : null
    },
    siteWhiteGloveOrders () {
      const id = Number(this.$route.params.siteId)
      return this.siteOrders.filter((o) => o.site_id === id)
    },
    wgProjectStatusRaw () {
      const sid = this.$route.params.siteId
      if (sid == null || sid === '') return null
      return this.siteProjectStatuses[String(sid)] || null
    },
    wgOverviewHeroMessages () {
      const toMsg = (row) => ({
        title: row.overview_title || '',
        body: row.overview_body || '',
        ctaLabel: row.primary_cta_label || '',
        ctaRoute: row.primary_cta_route || '',
        ctaStyle: row.cta_style || 'filled',
        secondaryCtaLabel: row.secondary_cta_label || '',
        secondaryCtaRoute: row.secondary_cta_route || '',
        secondaryCtaStyle: row.cta_style_secondary || 'outlined',
        statusKey: row.status_key || '',
      })
      const st = this.wgProjectStatusRaw
      if (st) {
        const raw = st.overview_messages
        if (Array.isArray(raw) && raw.length > 0) {
          return raw.map(toMsg)
        }
        return [toMsg(st)]
      }
      const n = this.overviewStats.contentPlansCount
      const planWord = n === 1 ? 'plan' : 'plans'
      const reviewTarget = n === 1 ? 'it' : 'them'
      return [
        {
          title: 'Your content plans have been delivered',
          body:
            `We've prepared ${n} content ${planWord} for your site. Review ${reviewTarget} and order content when ready`,
          ctaLabel: 'View Content Plans',
          ctaRoute: 'ContentPlansWhiteGlove',
          ctaStyle: 'filled',
          secondaryCtaLabel: '',
          secondaryCtaRoute: '',
          secondaryCtaStyle: 'outlined',
          statusKey: '',
        },
      ]
    },
    wgOverviewHeroClampedIndex () {
      const msgs = this.wgOverviewHeroMessages
      const n = msgs.length
      if (!n) return 0
      return Math.min(Math.max(0, this.wgOverviewHeroIndex), n - 1)
    },
    wgOverviewHero () {
      const msgs = this.wgOverviewHeroMessages
      const i = this.wgOverviewHeroClampedIndex
      return (
        msgs[i] || {
          title: '',
          body: '',
          ctaLabel: '',
          ctaRoute: '',
          ctaStyle: 'filled',
          secondaryCtaLabel: '',
          secondaryCtaRoute: '',
          secondaryCtaStyle: 'outlined',
          statusKey: '',
        }
      )
    },
    wgOverviewHeroPagerVisible () {
      return this.wgOverviewHeroMessages.length > 1
    },
    wgOverviewHeroPagerLabel () {
      const n = this.wgOverviewHeroMessages.length
      if (n <= 1) return ''
      const cur = this.wgOverviewHeroClampedIndex + 1
      return `Status ${cur}/${n}`
    },
    overviewPrimaryCtaClass () {
      const r = this.wgOverviewHero.ctaRoute
      return this.getOverviewHeroCtaClass(r, this.wgOverviewHero.ctaStyle)
    },
    overviewSecondaryCtaClass () {
      const r = this.wgOverviewHero.secondaryCtaRoute
      return this.getOverviewHeroCtaClass(r, this.wgOverviewHero.secondaryCtaStyle)
    },
    overviewPrimaryCtaTo () {
      const r = this.wgOverviewHero.ctaRoute
      return this.getOverviewHeroCtaTo(r)
    },
    overviewSecondaryCtaTo () {
      const r = this.wgOverviewHero.secondaryCtaRoute
      return this.getOverviewHeroCtaTo(r)
    },
    siteAuditOrders () {
      const rows = this.siteWhiteGloveOrders.filter((o) => o.type === 'audit_order')
      return [...rows].sort((a, b) => orderSortTs(b) - orderSortTs(a))
    },
    isEmpty () {
      return !this.ordersLoading && this.siteWhiteGloveOrders.length === 0
    },
    overviewStats () {
      const contentOrders = this.siteWhiteGloveOrders.filter((o) => CONTENT_ORDER_TYPES.includes(o.type))
      const confirmedContentOrders = contentOrders.filter(contentOrderIsConfirmed)
      const orderIds = new Set(confirmedContentOrders.map((o) => o.id))
      let articlesDelivered = 0
      let articlesInProgress = 0
      for (const o of contentOrders) {
        const n = contentOrderKeywordCount(o)
        const st = contentOrderFulfillmentStatus(o)
        if (st === 'completed') {
          articlesDelivered += n
        } else if (st === 'pending' || st === 'in_progress') {
          articlesInProgress += n
        }
      }
      const contentPlansCount =
        typeof this.contentPlansSiloCount === 'number'
          ? this.contentPlansSiloCount
          : contentOrders.length
      return {
        contentPlansCount,
        contentOrdered: confirmedContentOrders.reduce((sum, o) => sum + contentOrderKeywordCount(o), 0),
        ordersCount: orderIds.size || 0,
        articlesDelivered,
        articlesInProgress,
      }
    },
    hasCollapsedTimeline () {
      return this.timelineEvents.length > 10
    },
    visibleTimelineEvents () {
      return this.showAllTimelineEvents ? this.timelineEvents : this.timelineEvents.slice(0, 10)
    },
    timelineEvents () {
      const audits = this.siteAuditOrders
      const multiAudit = audits.length > 1
      const auditSuffix = (audit) => (multiAudit ? ` (Order #${audit.id})` : '')

      const contentPlansCreatedCount =
        this.contentPlansNotOrderedCount == null ? 0 : this.contentPlansNotOrderedCount

      let latestDeliveredAuditId = null
      let maxDeliveredSortTs = -Infinity
      for (const a of audits) {
        const st = a.fulfillement_status || a.fulfillementStatus
        if (st !== 'completed' || !a.auditDeliveredAt) continue
        const deliveredTs = dateToTimelineSortTs(a.auditDeliveredAt)
        const sortDelivered = deliveredTs || auditOrderPaidSortTs(a)
        const tieBreakWins =
          sortDelivered === maxDeliveredSortTs &&
          latestDeliveredAuditId != null &&
          a.id > latestDeliveredAuditId
        if (sortDelivered > maxDeliveredSortTs || tieBreakWins) {
          maxDeliveredSortTs = sortDelivered
          latestDeliveredAuditId = a.id
        }
      }

      const rows = []

      for (const audit of audits) {
        const suffix = auditSuffix(audit)
        const fulfillmentStatus = audit.fulfillement_status || audit.fulfillementStatus
        const purchaseTs = auditOrderPaidSortTs(audit)
        const deliveredTs = audit.auditDeliveredAt ? dateToTimelineSortTs(audit.auditDeliveredAt) : 0
        const auditKey = (slug) => `audit-${audit.id}-${slug}`

        if (fulfillmentStatus === 'completed' && audit.auditDeliveredAt) {
          const plansSuffix =
            contentPlansCreatedCount > 0 && audit.id === latestDeliveredAuditId
              ? ` — ${contentPlansCreatedCount} plan${contentPlansCreatedCount === 1 ? '' : 's'} created`
              : ''
          rows.push({
            key: auditKey('delivered'),
            title: `Audit delivered${suffix}${plansSuffix}`,
            date: this.formatTimelineDate(audit.auditDeliveredAt),
            sortTs: deliveredTs || purchaseTs,
          })
        }

        if (fulfillmentStatus === 'in_progress') {
          let sortTsProgress = purchaseTs ? purchaseTs + 1 : 0
          if (deliveredTs && purchaseTs) {
            sortTsProgress = Math.floor((purchaseTs + deliveredTs) / 2)
          } else if (purchaseTs) {
            sortTsProgress = purchaseTs + 3 * 86400
          }
          rows.push({
            key: auditKey('in-progress'),
            title: `Audit in progress${suffix}`,
            date: this.formatTimelineMoment(purchaseTs, audit),
            sortTs: sortTsProgress,
          })
        }

        const price = audit.price_on_checkout
        const priceBit = price != null ? ` (${formatCurrencyUtil(price)})` : ''
        const speedy = Number(audit.isSpeedyDeliveryOrder) === 1
        const paidAtRaw = audit.order_paid_at || audit.orderPaidAt
        const purchasedDate = paidAtRaw
          ? this.formatTimelineDate(paidAtRaw) || this.formatTimelineMoment(purchaseTs, audit)
          : this.formatTimelineMoment(purchaseTs, audit)
        rows.push({
          key: auditKey('purchased'),
          title: `${speedy ? 'Speedy audit' : 'Audit'} purchased${priceBit}${suffix}`,
          date: purchasedDate,
          sortTs: purchaseTs,
        })

        if (auditHasCompletedOnboarding(audit)) {
          const setupRaw = audit.formSubmittedAt || audit.createdAt
          let setupTs = setupRaw ? dateToTimelineSortTs(setupRaw) : (purchaseTs ? purchaseTs - 1 : 0)
          if (purchaseTs && setupTs === purchaseTs) {
            setupTs = purchaseTs - 1e-6
          }
          rows.push({
            key: auditKey('setup'),
            title: `Project setup completed${suffix}`,
            date: this.formatTimelineDate(setupRaw) || this.formatTimelineMoment(setupTs, audit),
            sortTs: setupTs,
          })
        } else {
          // show a "Form completed, connect GSC next" timeline event with audit.formSubmittedAt
          rows.push({
            key: auditKey('form-completed'),
            title: `Form completed, connect GSC next${suffix}`,
            date: this.formatTimelineDate(audit.formSubmittedAt) || this.formatTimelineMoment(purchaseTs, audit),
            sortTs: dateToTimelineSortTs(audit.formSubmittedAt),
          })
        }
      }

      const contentOrdersForTimeline = this.siteWhiteGloveOrders.filter(
        (o) => CONTENT_ORDER_TYPES.includes(o.type) && contentOrderIsConfirmed(o),
      )
      const multiContent = contentOrdersForTimeline.length > 1
      const contentSuffix = (order) => (multiContent ? ` (Order #${order.id})` : '')

      for (const o of contentOrdersForTimeline) {
        const n = contentOrderKeywordCount(o)
        const sortTs = contentOrderPaidSortTs(o)
        const paidAtRaw = o.order_paid_at || o.orderPaidAt
        const dateStr = paidAtRaw
          ? this.formatTimelineDate(paidAtRaw) || this.formatTimelineMoment(sortTs, o)
          : this.formatTimelineMoment(sortTs, o)
        const sfx = contentSuffix(o)
        const st = contentOrderFulfillmentStatus(o)

        if (st === 'in_progress') {
          rows.push({
            key: `content-${o.id}-ordered`,
            title: `Content writing in progress${sfx}`,
            date: dateStr,
            sortTs,
          })
        } else if (o.type === 'silo_order') {
          const planName = (o.silo && o.silo.name) || o.orderLabel || '—'
          const safePlan = String(planName).replace(/"/g, '\'')
          rows.push({
            key: `content-${o.id}-ordered`,
            title: `Content ordered — "${safePlan}" (${n} article${n === 1 ? '' : 's'})${sfx}`,
            date: dateStr,
            sortTs,
          })
        } else if (o.type === 'quick_content_order') {
          const price = o.price_on_checkout
          const priceBit = price != null ? `, ${formatCurrencyUtil(price)}` : ''
          rows.push({
            key: `content-${o.id}-ordered`,
            title: `Content ordered (${n} article${n === 1 ? '' : 's'}${priceBit})${sfx}`,
            date: dateStr,
            sortTs,
          })
        }
      }

      for (const o of contentOrdersForTimeline) {
        if (contentOrderFulfillmentStatus(o) !== 'completed') continue
        const completedRaw = o.completed_at || o.completedAt
        if (!completedRaw) continue

        const n = contentOrderKeywordCount(o)
        const sortTs = contentOrderCompletedSortTs(o)
        const dateStr =
          this.formatTimelineDate(completedRaw) || this.formatTimelineMoment(sortTs, o)

        rows.push({
          key: `content-${o.id}-delivered`,
          title: `${n} article${n === 1 ? '' : 's'} delivered${contentSuffix(o)}`,
          date: dateStr,
          sortTs,
        })
      }

      if (this.wgProjectStatusRaw?.status_key === 'all_delivered') {
        let bestSortTs = -Infinity
        let bestDate = ''

        for (const audit of audits) {
          const fulfillmentStatus = audit.fulfillement_status || audit.fulfillementStatus
          if (fulfillmentStatus !== 'completed' || !audit.auditDeliveredAt) continue
          const purchaseTs = auditOrderPaidSortTs(audit)
          const deliveredTs = dateToTimelineSortTs(audit.auditDeliveredAt)
          const sortTs = deliveredTs || purchaseTs
          const dateStr =
            this.formatTimelineDate(audit.auditDeliveredAt) ||
            this.formatTimelineMoment(sortTs, audit)
          if (sortTs > bestSortTs) {
            bestSortTs = sortTs
            bestDate = dateStr
          }
        }

        for (const o of contentOrdersForTimeline) {
          if (contentOrderFulfillmentStatus(o) !== 'completed') continue
          const completedRaw = o.completed_at || o.completedAt
          if (!completedRaw) continue
          const sortTs = contentOrderCompletedSortTs(o)
          const dateStr =
            this.formatTimelineDate(completedRaw) || this.formatTimelineMoment(sortTs, o)
          if (sortTs > bestSortTs) {
            bestSortTs = sortTs
            bestDate = dateStr
          }
        }

        if (bestSortTs !== -Infinity) {
          rows.push({
            key: 'all-content-delivered',
            title: 'All content delivered',
            date: bestDate || this.formatTimelineMoment(bestSortTs, null),
            sortTs: bestSortTs + 60,
          })
        }
      }

      rows.sort((a, b) => b.sortTs - a.sortTs)
      return rows.map(({ key, title, date, sortTs }) => ({ key, title, date, sortTs }))
    },
  },
  mounted () {
    this.loadSiteOrders()
    this.loadMissionControlSiteStatsIfNeeded()
  },
  methods: {
    formatCurrency (amount, emptyValue) {
      return formatCurrencyUtil(amount, emptyValue)
    },
    loadMissionControlSiteStatsIfNeeded () {
      if (!this.whiteGloveMissionControlAdminUi || !this.site?.id) {
        this.missionControlStatsLoading = false
        return
      }
      this.missionControlStatsLoading = true
      this.$store
        .dispatch('getWhiteGloveMissionControlSiteStats', { siteId: this.site.id })
        .then((response) => {
          const d = response.data
          if (d && d.status === 'SUCCESS') {
            this.missionControlPopRunsTotal = Number(d.popRunsTotal) || 0
            this.missionControlPopRunsInProgress = Number(d.popRunsInProgress) || 0
            this.missionControlMonthlySpendUsd =
              d.monthlySpendUsd != null && d.monthlySpendUsd !== ''
                ? Number(d.monthlySpendUsd)
                : null
          } else {
            this.missionControlPopRunsTotal = 0
            this.missionControlPopRunsInProgress = 0
            this.missionControlMonthlySpendUsd = null
          }
        })
        .catch(() => {
          this.missionControlPopRunsTotal = 0
          this.missionControlPopRunsInProgress = 0
          this.missionControlMonthlySpendUsd = null
        })
        .finally(() => {
          this.missionControlStatsLoading = false
        })
    },
    loadSiteOrders () {
      this.ordersLoading = true
      this.showAllTimelineEvents = false
      const siteId = Number(this.$route.params.siteId)
      Promise.all([
        this.$store.dispatch('getWhiteGloveOrders'),
        this.$store.dispatch('getSilos', {
          siteId,
          paginate: 0,
          // only silo count + content_report_order status are read here
          includeKeywords: 0,
          includeAiAndPageRunResult: 0,
          includePage: 0,
        }),
      ])
        .then(([ordersRes, silosRes]) => {
          const parsed = parseWhiteGloveOrdersResponse(ordersRes)
          this.siteOrders = parsed.orders
          this.siteProjectStatuses = parsed.siteProjectStatuses
          this.wgOverviewHeroIndex = 0
          const silos = (silosRes && silosRes.data && silosRes.data.data) || []
          this.contentPlansSiloCount = Array.isArray(silos) ? silos.length : null
          this.contentPlansNotOrderedCount = Array.isArray(silos)
            ? silos.filter((s) => !siloContentPlanOrderedLike(s)).length
            : null
        })
        .catch(() => {
          this.siteOrders = []
          this.siteProjectStatuses = {}
          this.wgOverviewHeroIndex = 0
          this.contentPlansSiloCount = null
          this.contentPlansNotOrderedCount = null
        })
        .finally(() => {
          this.ordersLoading = false
        })
    },
    stepWgOverviewHero (delta) {
      const msgs = this.wgOverviewHeroMessages
      const n = msgs.length
      if (n <= 1 || !delta) return
      const cur = this.wgOverviewHeroClampedIndex
      this.wgOverviewHeroIndex = (cur + delta + n) % n
    },
    navigateToOverviewRoute (route) {
      const to = this.getOverviewHeroCtaTo(route)
      if (to) {
        this.$router.push(to)
      }
    },
    getOverviewHeroCtaTo (route) {
      if (!route) return null
      if (route.startsWith('/')) return route
      return {
        name: route,
        params: { siteId: String(this.$route.params.siteId) },
      }
    },
    getOverviewHeroCtaClass (route, style = 'filled') {
      const isBlue = (
        route === 'OrderHistoryWhiteGlove'
        || route === 'AllContentWhiteGlove'
        || route.includes('/content-plans/')
        || route.includes('/all-content/quick/')
      )
      const normalizedStyle = ['outline', 'outlined'].includes(style) ? 'outline' : 'filled'
      if (isBlue) {
        return normalizedStyle === 'outline'
          ? 'wg-overview--populated__btn--blue-outline'
          : 'wg-overview--populated__btn--blue'
      }
      return normalizedStyle === 'outline'
        ? 'wg-overview--populated__btn--orange-outline'
        : 'wg-overview--populated__btn--orange'
    },
    formatTimelineDate (value) {
      const m = parseTimelineUtcMoment(value)
      return m ? m.local().format('MMM. D, YYYY') : ''
    },
    formatTimelineMoment (sortTs, fallbackOrder) {
      if (sortTs) return moment.unix(sortTs).format('MMM. D, YYYY')
      return this.formatTimelineDate(fallbackOrder?.order_date || fallbackOrder?.createdAt)
    },
    saveGoogleFolderUrl () {
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('updateSite', {
          siteId: this.site.id,
          body: { google_docs_folder_url: this.googleFolderUrlDraft },
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            return this.loadSitesAfterFolderSave().then(() => {
              this.$notify({ group: 'info', type: 'success', text: 'Successfully updated URL.' })
            })
          }
          this.$notify({ group: 'info', type: 'error', text: 'Failed to save' })
        })
        .catch(() => {
          this.$notify({ group: 'info', type: 'error', text: 'An error happened.' })
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    loadSitesAfterFolderSave () {
      return this.$store.dispatch('loadSites').then((response) => {
        if (response.data && response.data.status === 'SUCCESS') {
          this.$store.commit('setSites', response.data.sites)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@keyframes wg-overview-skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}

.wg-overview__skeleton-line,
.wg-overview__skeleton-pill,
.wg-overview__skeleton-avatar,
.wg-overview__skeleton-dot {
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #e8e9ed 0%,
    #f2f3f6 45%,
    #e8e9ed 90%
  );
  background-size: 200% 100%;
  animation: wg-overview-skeleton-shimmer 1.35s ease-in-out infinite;
}

.wg-overview__skeleton-inner {
  padding: 24px 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.wg-overview__skeleton-top-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.wg-overview__skeleton-panel {
  flex: 1;
  min-width: 280px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  border-radius: 12px;
}

.wg-overview__skeleton-panel--lavender {
  background: var(--v-pink1Color-base, #f7f7fc);
}

.wg-overview__skeleton-panel--bordered {
  background: #fff;
  border: 1px solid var(--v-gray12Color-base);
}

.wg-overview__skeleton-panel-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wg-overview__skeleton-line--heading {
  height: 22px;
  width: 85%;
  max-width: 360px;
}

.wg-overview__skeleton-line--body {
  height: 14px;
  width: 100%;
}

.wg-overview__skeleton-line--short {
  width: 70%;
}

.wg-overview__skeleton-pill {
  flex-shrink: 0;
  width: 160px;
  height: 44px;
  border-radius: 30px;
}

.wg-overview__skeleton-deliverables {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.wg-overview__skeleton-avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
}

.wg-overview__skeleton-section {
  margin: 0;
}

.wg-overview__skeleton-timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 6px;
}

.wg-overview__skeleton-timeline-item {
  display: flex;
  gap: 16px;
  padding-bottom: 20px;

  &:last-child {
    padding-bottom: 0;
  }
}

.wg-overview__skeleton-dot {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 4px;
  border-radius: 50%;
}

.wg-overview__skeleton-timeline-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wg-overview__skeleton-line--timeline-title {
  height: 16px;
  width: 75%;
}

.wg-overview__skeleton-line--timeline-date {
  height: 12px;
  width: 120px;
}

.wg-overview__skeleton-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.wg-overview__skeleton-stat-card {
  flex: 1;
  min-width: 160px;
  padding: 22px 20px;
  border-radius: 12px;
  background: var(--v-pink1Color-base, #f7f7fc);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wg-overview__skeleton-line--stat-value {
  height: 36px;
  width: 48px;
}

.wg-overview__skeleton-line--stat-label {
  height: 16px;
  width: 70%;
}

.wg-overview__skeleton-line--stat-sub {
  height: 12px;
  width: 55%;
}

/* Populated dashboard */
.wg-overview--populated {
  padding: 24px 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.wg-overview--populated__top-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.wg-overview--populated__panel {
  flex: 1;
  min-width: 280px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #D5D5D5;
}

.wg-overview--populated__panel--lavender {
  background: #F7F5FB;
}

.wg-overview--populated__panel--green {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
}

.wg-overview--populated__panel--bordered {
  background: #fff;
  border: 1px solid var(--v-gray12Color-base);
}

.wg-overview--populated__panel--mission-control {
  flex-direction: column;
  align-items: stretch;
  align-content: stretch;
}

.wg-overview--populated__deliverables-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
}

.wg-overview--populated__deliverables-edit-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.wg-overview--populated__folder-url-field-wrap {
  flex: 1;
  min-width: 0;
}

.wg-overview--populated__save-folder-btn {
  flex-shrink: 0;
  height: 40px !important;
}

.wg-overview--populated__panel-text {
  flex: 1;
  min-width: 0;
}

.wg-overview--populated__hero-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  flex-shrink: 0;
  width: max-content;
}

.wg-overview--populated__hero-btn {
  width: 100%;
  justify-content: space-between;
  text-align: left;
}

.wg-overview--populated__hero-btn-label {
  flex: 1;
  min-width: 0;
}

.wg-overview--populated__heading {
  margin: 0 0 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.35;
}

.wg-overview--populated__subtext {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--v-gray2Color-base, #757575);
}

.wg-overview--populated__hero-status-pager {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
}

.wg-overview--populated__hero-status-pager__angle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--v-link15Color-base, #0075ff);
  border-radius: 4px;
  font-family: inherit;
  line-height: 1;
}

.wg-overview--populated__hero-status-pager__angle:hover {
  opacity: 0.85;
  background: rgba(0, 117, 255, 0.08);
}

.wg-overview--populated__hero-status-pager__icon {
  font-size: 1.5rem;
}

.wg-overview--populated__hero-status-pager__label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--v-gray2Color-base, #757575);
  padding: 0 4px;
  user-select: none;
}

.wg-overview--populated__deliverables-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.wg-overview--populated__g-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: var(--v-link15Color-base, #0075ff);
  display: flex;
  align-items: center;
  justify-content: center;
}

.wg-overview--populated__g-letter {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: inherit;
  line-height: 1;
}

.wg-overview--populated__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 12px 22px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
  white-space: nowrap;

  .material-icons {
    font-size: 20px;
  }

  &:hover {
    opacity: 0.88;
  }
}

.wg-overview--populated__btn--orange {
  background: var(--v-profileAnchorColor-base);
  color: #fff;
}

.wg-overview--populated__btn--blue {
  background: var(--v-link15Color-base, #0075ff);
  color: #fff;
}

.wg-overview--populated__btn--orange-outline {
  background: transparent;
  color: var(--v-profileAnchorColor-base);
  border: 1px solid var(--v-profileAnchorColor-base);
}

.wg-overview--populated__btn--blue-outline {
  background: transparent;
  color: var(--v-link15Color-base, #0075ff);
  border: 1px solid var(--v-link15Color-base, #0075ff);
}

.wg-overview--populated__folder-unavailable {
  flex-shrink: 0;
  font-size: 0.85rem;
  color: var(--v-gray2Color-base, #757575);
}

.wg-overview--populated__timeline {
  list-style: none;
  margin: 0;
  padding: 0 0 0 6px;
  position: relative;
}

.wg-overview--populated__timeline-item {
  position: relative;
  display: flex;
  gap: 16px;
  padding-bottom: 20px;
  z-index: 1;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 6px;
    top: calc(4px + 12px + 6px);
    bottom: 6px;
    width: 1px;
    transform: translateX(-50%);
    background: var(--v-grayColor-base);
  }

  &:last-child {
    padding-bottom: 0;
  }
}

.wg-overview--populated__timeline-dot {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 4px;
  border-radius: 50%;
  background: #11A33D;
  box-shadow: 0px 0px 6px 0px #11A33D4D;
}

.wg-overview--populated__timeline-body {
  flex: 1;
  min-width: 0;
}

.wg-overview--populated__timeline-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.35;
}

.wg-overview--populated__timeline-date {
  margin-top: 4px;
  font-size: 0.85rem;
  color: var(--v-gray2Color-base, #757575);
}

.wg-overview--populated__timeline-header {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.35;
  margin-bottom: 20px;
}

.wg-overview--populated__timeline-toggle {
  margin-top: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.95rem;
  line-height: 1.35;
  cursor: pointer;
  color: var(--v-mainColor-base);
}

.wg-overview--populated__stats-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.wg-overview--populated__stat-card {
  min-width: 230px;
  padding: 22px 20px;
  border-radius: 12px;
  background: #F7F5FB;
  border: 1px solid #D5D5D5;
}

.wg-overview--populated__stat-card--link {
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.wg-overview--populated__stat-card--link:hover,
.wg-overview--populated__stat-card--link:focus-visible {
  border-color: var(--v-profileAnchorColor-base);
  box-shadow: 0 8px 20px rgba(17, 24, 39, 0.08);
  transform: translateY(-1px);
  outline: none;
}

.wg-overview--populated__stat-tooltip-trigger {
  display: inline-flex;
}

.wg-overview--populated__stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.2;
  margin-bottom: 10px;
}

.wg-overview--populated__stat-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
}

.wg-overview--populated__stat-sublabel {
  margin-top: 10px;
  font-size: 0.85rem;
  color: var(--v-gray2Color-base, #757575);
}

.wg-overview--populated__stat-card--mission-control {
  border: 1px solid #A124A8;
  .wg-overview--populated__stat-value,
  .wg-overview--populated__stat-label {
    color: var(--v-purpleColor1-base, #8e44ad);
  }
}
</style>
